import type {
  StripeCatalogResponse,
  StripeCheckoutSessionResponse,
  StripeCheckoutSessionResult,
  StripeCheckoutStatusResponse,
  StripeEntitlementResponse,
  StripeErrorResponse,
  StripePortalSessionResponse,
} from "store/billing/types"
import { getPlatformHeaders } from "./platformApiAuth"
import {
  isBillingPaywallError,
  syncBillingDenyState,
  syncBillingEntitlement,
} from "./platformBillingState"
import { getPlatformServiceEndpoint } from "./platformServiceEndpoints"

class BillingRequestError extends Error {
  data: StripeErrorResponse | null
  status: number

  constructor(message: string, status: number, data: StripeErrorResponse | null) {
    super(message)
    this.name = "BillingRequestError"
    this.data = data
    this.status = status
  }
}

export class BillingApi {
  private get billingEndpoint() {
    return getPlatformServiceEndpoint("billing")
  }

  private async parseBillingError(response: Response): Promise<StripeErrorResponse | null> {
    try {
      return (await response.json()) as StripeErrorResponse
    } catch {
      return null
    }
  }

  private async requestBillingJson<T>(
    path: string,
    options: {
      body?: string
      contentType?: string
      defaultErrorMessage: string
      method: "GET" | "POST"
      suppressPaywall?: boolean
    }
  ): Promise<T> {
    const response = await fetch(`${this.billingEndpoint}${path}`, {
      method: options.method,
      headers: await getPlatformHeaders(options.contentType),
      ...(options.body ? { body: options.body } : {}),
    })

    if (!response.ok) {
      const data = await this.parseBillingError(response)

      if (!options.suppressPaywall && isBillingPaywallError(data)) syncBillingDenyState(data!)

      throw new BillingRequestError(options.defaultErrorMessage, response.status, data)
    }

    return (await response.json()) as T
  }

  async stripeEntitlement(options?: {
    suppressPaywall?: boolean
  }): Promise<StripeEntitlementResponse> {
    const entitlement = await this.requestBillingJson<StripeEntitlementResponse>(
      "/customer/entitlement",
      {
        defaultErrorMessage: "We couldn't check your access. Please try again.",
        method: "GET",
        suppressPaywall: options?.suppressPaywall,
      }
    )

    syncBillingEntitlement(entitlement)
    return entitlement
  }

  async stripeCatalog(options?: { suppressPaywall?: boolean }): Promise<StripeCatalogResponse> {
    return this.requestBillingJson<StripeCatalogResponse>("/catalog", {
      defaultErrorMessage: "We couldn't load the plans. Please try again.",
      method: "GET",
      suppressPaywall: options?.suppressPaywall,
    })
  }

  async stripeCheckoutSession(
    priceId: string,
    options?: {
      returnPath?: string
      suppressPaywall?: boolean
    }
  ): Promise<StripeCheckoutSessionResult> {
    const response = await fetch(`${this.billingEndpoint}/checkout-session`, {
      method: "POST",
      headers: await getPlatformHeaders("application/json"),
      body: JSON.stringify({
        priceId,
        returnPath: options?.returnPath,
      }),
    })

    if (response.ok)
      return {
        ok: true,
        data: (await response.json()) as StripeCheckoutSessionResponse,
      }

    const data = await this.parseBillingError(response)
    if (!options?.suppressPaywall && isBillingPaywallError(data)) syncBillingDenyState(data!)

    return {
      ok: false,
      status: response.status,
      data,
    }
  }

  async stripePortalSession(options?: {
    returnPath?: string
    suppressPaywall?: boolean
  }): Promise<StripePortalSessionResponse> {
    return this.requestBillingJson<StripePortalSessionResponse>("/portal-session", {
      body: JSON.stringify({
        returnPath: options?.returnPath,
      }),
      contentType: "application/json",
      defaultErrorMessage: "We couldn't open your plan page. Please try again.",
      method: "POST",
      suppressPaywall: options?.suppressPaywall,
    })
  }

  async stripeCheckoutStatus(
    sessionId: string,
    options?: {
      suppressPaywall?: boolean
    }
  ): Promise<StripeCheckoutStatusResponse> {
    const query = new URLSearchParams({
      sessionId,
    })
    const status = await this.requestBillingJson<StripeCheckoutStatusResponse>(
      `/customer/checkout-status?${query.toString()}`,
      {
        defaultErrorMessage: "We couldn't check your payment yet. Please try again.",
        method: "GET",
        suppressPaywall: options?.suppressPaywall,
      }
    )

    syncBillingEntitlement(status.entitlement)
    return status
  }
}

export const billingApi = new BillingApi()
